import { imgixHost, isAdmin } from 'src/config'
import ConfigurationAudit from 'src/models/configuration-audit/ConfigurationAudit'
import Category from 'src/models/item/Category'
import ItemClass from 'src/models/item/ItemClass'
import ItemModifierSet from 'src/models/item/ItemModifierSet'
import ItemVariation from 'src/models/item/ItemVariation'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Tax from 'src/models/order/Tax'

const ACTIONS = {
  DISABLE_SALES: 'disable_sales',
  ENABLE_SALES: 'enable_sales'
}

export default class Item extends Model {
  static name = 'Item'

  static isConfiguration = true

  static duplicatable = true

  static deletable = true

  static versioned = true

  static paginate = true

  static hasAuditSummary = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.concerns = [Archivable]

    this.fields = {
      id: {
        type: ID,
        sortable: true,
        required: true
      },
      action: {
        type: String,
        input: true
      },

      name: {
        type: String,
        sortable: true,
        default: null,
        translatable: true
      },
      displayName: {
        type: String,
        default: null,
        translatable: true
      },
      abbreviation: {
        type: String
      },
      color: {
        type: String
      },
      description: {
        type: String
      },
      image: {
        type: String,
        default: null
      },
      salesDisabled: {
        type: Boolean,
        default: false
      },
      requiresPreparation: {
        type: Boolean,
        default: false
      },
      locations: {
        type: [Location],
        default: () => ([]),
        encode (data) {
          data.locationIds = _.map(this.locations.data, 'id')
        },
        reference: true
      },
      preparationTimeMinutes: {
        type: Integer,
        default: 0
      },

      hasCustomPrice: {
        type: Boolean,
        default: false
      },
      availableOnline: {
        type: Boolean,
        default: false
      },
      ignoreAlcoholAcknowledgement: {
        type: Boolean,
        default: false
      },
      isFeature: {
        type: Boolean,
        default: false
      },
      overrideSkipDetailDialog: {
        type: Boolean,
        default: false

      },
      customSkipDetailDialog: {
        type: Boolean,
        default: false
      },

      overrideTaxes: {
        type: Boolean,
        default: false
      },
      taxes: {
        type: [Tax],
        default: () => ([]),
        encode (data) {
          data.taxIds = _.map(this.taxes.data, 'id')
        },
        reference: true
      },

      itemClass: {
        type: ItemClass,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },

      category: {
        type: Category,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },

      preparationCategory: {
        type: Category,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },

      popularity: {
        type: Integer,
        output: true
      },

      itemVariations: {
        type: [ItemVariation],
        default: () => ([]),
        allowDestroy: true,
        reference: true,
        acceptsNestedAttributes: true
      },
      itemModifierSets: {
        type: [ItemModifierSet],
        allowDestroy: true
      },
      printingOptions: {
        type: JsonObject,
        default: () => ({
          print_name: true
        })
      },
      preparationOrder: {
        type: Integer,
        required: true,
        default: 100
      },

      libraryParentId: {
        type: ID,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        excludeFromDefault: true
      },
      libraryChildSiteIds: {
        type: [ID],
        output: true,
        excludeFromDefault: true
      },
      libraryDescendantSites: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },
      modelAudits: {
        type: [ConfigurationAudit],
        default: () => { return [] },
        output: true,
        excludeFromDefault: !isAdmin
      },
      priceCalculationCache: {
        type: JsonObject,
        local: true,
        default: () => ({})
      }
    }

    this.computed = {
      backgroundForImgix () {
        if (this.color) {
          return this.color.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=190&h=118&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}&auto=format`
      },
      highResImageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=600&h=373&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}&auto=format`
      },
      skipDetailDialog () {
        return this.overrideSkipDetailDialog ? this.customSkipDetailDialog : !this.itemModifierSets?.data?.length && this.activeItemVariations?.length === 1
      },
      taxable () {
        return this.taxes?.data?.length >= 1
      },
      activeItemVariations () {
        return _.filter(this.itemVariations?.data, { isActive: true })
      },
      availableOnlineItemModifierSets () {
        return _.filter(this.itemModifierSets?.data, { isAvailableOnline: true })
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      },
      salesDisabledByItemVariations () {
        return _.every(this.activeItemVariations, { salesDisabled: true })
      },
      salesDisabledByModifiers () {
        return _.some(this.itemModifierSets?.data, itemModifierSet => {
          if (!itemModifierSet.minSelectionCount) return false

          const numOfModifiersWithSalesEnabled = _.filter(itemModifierSet.modifierSet.modifiers.data, { salesDisabled: false }).length
          return numOfModifiersWithSalesEnabled < itemModifierSet.minSelectionCount
        })
      },
      salesUnavailable () {
        return this.salesDisabled || this.salesDisabledByItemVariations || this.salesDisabledByModifiers
      }
    }
  }

  getBestVoucherForItem (voucherTypes) {
    let bestVoucher = null
    _.each(voucherTypes, (voucherType) => {
      if (!voucherType.isItem(this)) {
        return
      }
      if (!voucherType.hasMaxPrice) {
        bestVoucher = voucherType
        return false
      } else if (!bestVoucher || bestVoucher.maxPrice < voucherType.maxPrice) {
        bestVoucher = voucherType
      }
    })
    return bestVoucher
  }

  primaryLocationForRegisterStation (currentRegisterStation) {
    const locationGroup = currentRegisterStation?.registerType?.location?.locationGroup

    const allSourceLocations = this.locations.data
    const sourceLocations = locationGroup
      ? _.filter(allSourceLocations, ({ locationGroup: { id } }) => id === locationGroup.id)
      : allSourceLocations

    return _.find(sourceLocations, 'hasPickPrepPrinter') || _.first(sourceLocations) || _.first(allSourceLocations)
  }

  hasModifierSet (modifierSet) {
    return _.some(this.itemModifierSets.data, (itemModifierSet) => itemModifierSet.modifierSetId === modifierSet.id)
  }

  hasSavedModifierSet (modifierSet) {
    const itemModifierSet = this.itemModifierSetByModifierSet(modifierSet)
    return itemModifierSet && !itemModifierSet.isLocal
  }

  itemModifierSetByModifierSet (modifierSet) {
    return _.find(this.itemModifierSets.data, (itemModifierSet) => itemModifierSet.modifierSetId === modifierSet.id)
  }

  addModifierSet (modifierSet) {
    if (this.hasModifierSet(modifierSet)) {
      return
    }

    const itemModifierSet = new ItemModifierSet({ modifierSetId: modifierSet.id })

    this.itemModifierSets.add(itemModifierSet)
  }

  removeModifierSet (modifierSet) {
    this.itemModifierSets.filter(itemModifierSet => itemModifierSet.modifierSetId !== modifierSet.id)
  }

  get searchString () {
    return _([this.name, this.itemClass?.name]).compact().join(' ').toLowerCase()
  }

  get allModifiers () {
    return _(this.itemModifierSets.data).map('modifierSet.modifiers.data').flatten().compact().value()
  }

  get allItemModifiers () {
    return _(this.itemModifierSets.data).map(itemModifierSet => {
      const modifierSet = itemModifierSet.modifierSet
      return _.map(modifierSet.modifiers.data, modifier => {
        return {
          modifier,
          modifierSet
        }
      })
    }).flatten().compact().value()
  }

  hasLocation (location) {
    if (!location) return false

    return _.some(this.locations?.data, { id: location.id })
  }

  clone () {
    const item = super.clone()
    item.priceCalculationCache = {}
    return item
  }

  getPriceForOrder (order) {
    if (!_.has(this.priceCalculationCache, 'price')) {
      let orderItem
      if (this.itemPreference) {
        orderItem = order.getFavoriteOrderItem(this.itemPreference)
      } else {
        orderItem = order.itemOrderItemForItem(this)
      }
      const price = orderItem?.displayPrice || 0
      const itemName = orderItem?.itemOrderItem?.item?.name

      this.hasExtraCharge =
    (this.itemVariations?.data?.length > 1) ||
    (orderItem?.itemOrderItem?.itemModifiers?.data?.length)

      orderItem?.destroy()
      this.priceCalculationCache.price = price
      this.priceCalculationCache.description = itemName
    }

    return {
      price: this.priceCalculationCache.price,
      description: this.priceCalculationCache.description
    }
  }

  get showAlcoholAcknowledgement () {
    if (!this.category) return false

    return this.category && this.category.hasAlcoholAcknowledgement && !this.ignoreAlcoholAcknowledgement
  }

  async disableSales () {
    this.action = ACTIONS.DISABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async enableSales () {
    this.action = ACTIONS.ENABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
